import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { client } from '../../api/client';
import { Pagination } from '../common/Pagination';

interface ListingOwner {
  id: number;
  name: string;
  email: string;
}

interface Listing {
  id: number;
  user_id: number;
  title: string;
  description: string;
  ad_kind: 'Ponuda' | 'Potražnja' | 'Aukcija';
  ad_type: 'Prodaja' | 'Izdavanje';
  category: string;
  price: number;
  price_min?: number;
  price_max?: number;
  currency: string;
  region: string;
  city: string;
  status: 'active' | 'pending' | 'rejected' | 'suspended' | 'expired';
  approved: boolean;
  created_at: string;
  expires_at?: string;
  duration_days?: number;
  user_name?: string;
  user_email?: string;
  admin_note?: string;
  owner?: ListingOwner;
}

// Helper function to calculate remaining days
function getRemainingDays(expiresAt?: string): number | null {
  if (!expiresAt) return null;
  const expiry = new Date(expiresAt);
  const now = new Date();
  const diffTime = expiry.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
}

export function ListingsTab() {
  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('all'); // 'all', 'today', 'week', 'month'
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Modal States
  const [suspendModalOpen, setSuspendModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [reactivateModalOpen, setReactivateModalOpen] = useState(false);
  const [extendModalOpen, setExtendModalOpen] = useState(false);
  const [selectedListingId, setSelectedListingId] = useState<number | null>(null);
  const [actionReason, setActionReason] = useState('');
  const [reactivateDays, setReactivateDays] = useState(30);
  const [extendDays, setExtendDays] = useState(30);

  useEffect(() => {
    loadListings();
  }, []);

  const loadListings = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await client.api.fetch('/admin/listings');
      const data = await res.json();
      if (data.success) {
        setListings(data.data || []);
      } else {
        setError(data.error || 'Greška pri učitavanju oglasa');
      }
    } catch (err) {
      console.error('Error loading listings:', err);
      setError('Greška pri povezivanju sa serverom');
    } finally {
      setLoading(false);
    }
  };

  const filteredListings = listings.filter(listing => {
    const matchesSearch = listing.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          listing.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || listing.ad_kind === typeFilter;
    const matchesStatus = statusFilter === 'all' ||
                          (statusFilter === 'pending' && listing.status === 'pending') ||
                          (statusFilter === 'active' && listing.status === 'active') ||
                          (statusFilter === 'rejected' && listing.status === 'rejected') ||
                          (statusFilter === 'suspended' && listing.status === 'suspended') ||
                          (statusFilter === 'expired' && listing.status === 'expired');
    const ownerSearchLower = ownerFilter.toLowerCase().trim();
    const matchesOwner = ownerFilter === '' || 
                         (listing.owner && (
                           listing.owner.name.toLowerCase().includes(ownerSearchLower) ||
                           listing.owner.email.toLowerCase().includes(ownerSearchLower)
                         ));
    
    // Date filter
    let matchesDate = true;
    if (dateFilter !== 'all' && listing.created_at) {
      const createdDate = new Date(listing.created_at);
      const now = new Date();
      const diffDays = Math.floor((now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
      
      if (dateFilter === 'today') {
        matchesDate = diffDays === 0;
      } else if (dateFilter === 'week') {
        matchesDate = diffDays <= 7;
      } else if (dateFilter === 'month') {
        matchesDate = diffDays <= 30;
      }
    }

    return matchesSearch && matchesType && matchesStatus && matchesOwner && matchesDate;
  });

  const totalPages = Math.ceil(filteredListings.length / itemsPerPage);
  const paginatedListings = filteredListings.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleApprove = async (id: number) => {
    try {
      const res = await client.api.fetch(`/admin/listings/${id}/approve`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        setListings(listings.map(l => l.id === id ? { ...l, status: 'active', approved: true } : l));
      } else {
        alert(data.error || 'Greška pri odobravanju oglasa');
      }
    } catch (err) {
      alert('Greška pri povezivanju sa serverom');
    }
  };

  const handleReject = async () => {
    if (!selectedListingId) return;

    try {
      const res = await client.api.fetch(`/admin/listings/${selectedListingId}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reason: actionReason })
      });
      const data = await res.json();
      if (data.success) {
        setListings(listings.map(l => l.id === selectedListingId ? { ...l, status: 'rejected', approved: false } : l));
        setRejectModalOpen(false);
        setSelectedListingId(null);
        setActionReason('');
      } else {
        alert(data.error || 'Greška pri odbijanju oglasa');
      }
    } catch (err) {
      alert('Greška pri povezivanju sa serverom');
    }
  };

  const handleSuspend = async () => {
    if (!selectedListingId || !actionReason) return;

    try {
      const res = await client.api.fetch(`/admin/listings/${selectedListingId}/suspend`, {
        method: 'POST',
        body: JSON.stringify({ reason: actionReason })
      });
      const data = await res.json();
      if (data.success) {
        setListings(listings.map(l => l.id === selectedListingId ? { ...l, status: 'suspended', approved: false, admin_note: actionReason } : l));
        setSuspendModalOpen(false);
        setSelectedListingId(null);
        setActionReason('');
      } else {
        alert(data.error || 'Greška pri suspendovanju oglasa');
      }
    } catch (err) {
      alert('Greška pri povezivanju sa serverom');
    }
  };

  const handleReactivate = async (id: number) => {
    if (!window.confirm('Da li ste sigurni da želite da reaktivirate ovaj oglas?')) return;

    try {
      const res = await client.api.fetch(`/admin/listings/${id}/reactivate`, { 
        method: 'POST',
        body: JSON.stringify({ duration_days: reactivateDays })
      });
      const data = await res.json();
      if (data.success) {
        setListings(listings.map(l => l.id === id ? { ...l, status: 'active', approved: true } : l));
        setReactivateModalOpen(false);
        setSelectedListingId(null);
        setReactivateDays(30);
      } else {
        alert(data.error || 'Greška pri reaktivaciji oglasa');
      }
    } catch (err) {
      alert('Greška pri povezivanju sa serverom');
    }
  };

  const openReactivateModal = (id: number) => {
    setSelectedListingId(id);
    setReactivateDays(30);
    setReactivateModalOpen(true);
  };

  const confirmReactivate = async () => {
    if (!selectedListingId) return;
    
    try {
      const res = await client.api.fetch(`/admin/listings/${selectedListingId}/reactivate`, { 
        method: 'POST',
        body: JSON.stringify({ duration_days: reactivateDays })
      });
      const data = await res.json();
      if (data.success) {
        setListings(listings.map(l => l.id === selectedListingId ? { ...l, status: 'active', approved: true } : l));
        setReactivateModalOpen(false);
        setSelectedListingId(null);
        setReactivateDays(30);
      } else {
        alert(data.error || 'Greška pri reaktivaciji oglasa');
      }
    } catch (err) {
      alert('Greška pri povezivanju sa serverom');
    }
  };

  const openExtendModal = (id: number) => {
    setSelectedListingId(id);
    setExtendDays(30);
    setExtendModalOpen(true);
  };

  const confirmExtend = async () => {
    if (!selectedListingId) return;
    
    try {
      const res = await client.api.fetch(`/admin/listings/${selectedListingId}/extend`, { 
        method: 'POST',
        body: JSON.stringify({ duration_days: extendDays })
      });
      const data = await res.json();
      if (data.success) {
        loadListings(); // Reload to get updated expires_at
        setExtendModalOpen(false);
        setSelectedListingId(null);
        setExtendDays(30);
      } else {
        alert(data.error || 'Greška pri produženju oglasa');
      }
    } catch (err) {
      alert('Greška pri povezivanju sa serverom');
    }
  };

  const handleDelete = async (id: number) => {
    console.log('[Admin] Delete requested for listing:', id);
    if (!window.confirm('Da li ste sigurni da želite da obrišete ovaj oglas? Ova akcija se ne može poništiti.')) {
      console.log('[Admin] Delete cancelled by user');
      return;
    }

    try {
      console.log('[Admin] Sending DELETE request for listing:', id);
      const res = await client.api.fetch(`/admin/listings/${id}`, { method: 'DELETE' });
      const data = await res.json();
      console.log('[Admin] Delete response:', data);
      if (data.success) {
        setListings(listings.filter(l => l.id !== id));
        console.log('[Admin] Listing deleted successfully');
      } else {
        alert(data.error || 'Greška pri brisanju oglasa');
      }
    } catch (err) {
      console.error('[Admin] Delete error:', err);
      alert('Greška pri povezivanju sa serverom');
    }
  };

  const openSuspendModal = (id: number) => {
    setSelectedListingId(id);
    setActionReason('');
    setSuspendModalOpen(true);
  };

  const openRejectModal = (id: number) => {
    setSelectedListingId(id);
    setActionReason('');
    setRejectModalOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 p-4 rounded-lg">
          {error}
          <button onClick={loadListings} className="ml-4 underline">Pokušaj ponovo</button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-4 bg-white dark:bg-slate-800 p-4 rounded-xl shadow-sm">
        <div className="flex-1 min-w-[200px]">
          <input
            type="text"
            placeholder="Pretraži oglase..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
          />
        </div>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
        >
          <option value="all">Svi tipovi</option>
          <option value="Ponuda">Ponuda</option>
          <option value="Potražnja">Potražnja</option>
          <option value="Aukcija">Aukcija</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
        >
          <option value="all">Svi statusi</option>
          <option value="pending">Na čekanju</option>
          <option value="active">Aktivni</option>
          <option value="rejected">Odbijeni</option>
          <option value="suspended">Suspendovani</option>
          <option value="expired">Istekli</option>
        </select>
        <select
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
          className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
        >
          <option value="all">Svi datumi</option>
          <option value="today">Danas</option>
          <option value="week">Poslednjih 7 dana</option>
          <option value="month">Poslednjih 30 dana</option>
        </select>
        <input
          type="text"
          placeholder="Pretraži po vlasniku (ime ili email)..."
          value={ownerFilter}
          onChange={(e) => setOwnerFilter(e.target.value)}
          className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white min-w-[250px]"
        />
      </div>

      {/* Listings Table */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-50 dark:bg-slate-700/50">
              <tr>
                <th className="p-4 font-semibold text-slate-900 dark:text-white">Naslov</th>
                <th className="p-4 font-semibold text-slate-900 dark:text-white">Vlasnik</th>
                <th className="p-4 font-semibold text-slate-900 dark:text-white">Tip</th>
                <th className="p-4 font-semibold text-slate-900 dark:text-white">Cena</th>
                <th className="p-4 font-semibold text-slate-900 dark:text-white">Kreirano</th>
                <th className="p-4 font-semibold text-slate-900 dark:text-white">Ističe</th>
                <th className="p-4 font-semibold text-slate-900 dark:text-white">Status</th>
                <th className="p-4 font-semibold text-slate-900 dark:text-white">Akcije</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {paginatedListings.length === 0 ? (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-slate-500 dark:text-slate-400">
                    Nema oglasa koji odgovaraju filterima.
                  </td>
                </tr>
              ) : (
                paginatedListings.map((listing) => (
                  <tr key={listing.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                    <td className="p-4">
                      <div className="font-medium text-slate-900 dark:text-white line-clamp-1">{listing.title}</div>
                      <div className="text-sm text-slate-500">{listing.category} - {listing.city}</div>
                      {listing.status === 'suspended' && listing.admin_note && (
                        <div className="text-xs text-red-500 mt-1">Razlog: {listing.admin_note}</div>
                      )}
                    </td>
                    <td className="p-4">
                      {listing.owner ? (
                        <div>
                          <div className="font-medium text-slate-900 dark:text-white text-sm">{listing.owner.name}</div>
                          <div className="text-xs text-slate-500">{listing.owner.email}</div>
                        </div>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        listing.ad_kind === 'Ponuda' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                        listing.ad_kind === 'Potražnja' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                        'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                      }`}>
                        {listing.ad_kind}
                      </span>
                    </td>
                    <td className="p-4 font-medium text-slate-900 dark:text-white">
                      {listing.ad_kind === 'Potražnja' && (listing.price_min || listing.price_max) ? (
                        <>
                          {listing.price_min ? listing.price_min.toLocaleString() : '0'}
                          {' - '}
                          {listing.price_max ? listing.price_max.toLocaleString() : '∞'}
                          {' '}{listing.currency}
                        </>
                      ) : (
                        <>{listing.price.toLocaleString()} {listing.currency}</>
                      )}
                    </td>
                    <td className="p-4 text-sm text-slate-600 dark:text-slate-300">
                      {listing.created_at ? new Date(listing.created_at).toLocaleDateString('sr-RS', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      }) : '-'}
                    </td>
                    <td className="p-4">
                      {(() => {
                        const remainingDays = getRemainingDays(listing.expires_at);
                        if (remainingDays === null) {
                          return <span className="text-slate-400">-</span>;
                        }
                        if (remainingDays <= 0) {
                          return <span className="text-red-500 font-medium">Istekao</span>;
                        }
                        if (remainingDays <= 7) {
                          return <span className="text-orange-500 font-medium">{remainingDays} dana</span>;
                        }
                        return <span className="text-slate-600 dark:text-slate-300">{remainingDays} dana</span>;
                      })()}
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        listing.status === 'active'
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          : listing.status === 'rejected'
                          ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                          : listing.status === 'suspended'
                          ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                          : listing.status === 'expired'
                          ? 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400'
                          : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                      }`}>
                        {listing.status === 'active' ? 'Aktivan' :
                         listing.status === 'rejected' ? 'Odbijen' :
                         listing.status === 'suspended' ? 'Suspendovan' :
                         listing.status === 'expired' ? 'Istekao' : 'Na čekanju'}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex gap-2 flex-wrap">
                        {listing.status === 'pending' && (
                          <>
                            <button
                              type="button"
                              onClick={() => handleApprove(listing.id)}
                              className="text-green-600 hover:text-green-500 font-medium text-sm"
                            >
                              Odobri
                            </button>
                            <button
                              type="button"
                              onClick={() => openRejectModal(listing.id)}
                              className="text-red-600 hover:text-red-500 font-medium text-sm"
                            >
                              Odbij
                            </button>
                          </>
                        )}
                        {listing.status === 'active' && (
                          <>
                            <button
                              type="button"
                              onClick={() => openExtendModal(listing.id)}
                              className="text-blue-600 hover:text-blue-500 font-medium text-sm"
                            >
                              Produži
                            </button>
                            <button
                              type="button"
                              onClick={() => openSuspendModal(listing.id)}
                              className="text-orange-600 hover:text-orange-500 font-medium text-sm"
                            >
                              Suspenduj
                            </button>
                          </>
                        )}
                        {(listing.status === 'suspended' || listing.status === 'rejected' || listing.status === 'expired') && (
                          <button
                            type="button"
                            onClick={() => openReactivateModal(listing.id)}
                            className="text-green-600 hover:text-green-500 font-medium text-sm"
                          >
                            Reaktiviraj
                          </button>
                        )}
                        <Link
                          to={`/${listing.ad_kind === 'Aukcija' ? 'aukcije' : listing.ad_kind === 'Potražnja' ? 'potraznja' : 'ponuda'}/${listing.id}`}
                          target="_blank"
                          className="text-blue-600 hover:text-blue-500 font-medium text-sm"
                        >
                          Pogledaj
                        </Link>
                        <button
                          type="button"
                          onClick={() => handleDelete(listing.id)}
                          className="text-slate-500 hover:text-red-500 transition-colors"
                          title="Obriši oglas"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          itemsPerPage={itemsPerPage}
          onItemsPerPageChange={setItemsPerPage}
          totalItems={filteredListings.length}
        />
      </div>

      {/* Suspend Modal */}
      {suspendModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Suspenduj oglas</h3>
            <p className="text-slate-600 dark:text-slate-400 mb-4">
              Unesite razlog suspenzije. Korisnik će biti obavešten i moraće da izmeni oglas.
            </p>
            <textarea
              value={actionReason}
              onChange={(e) => setActionReason(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white mb-4 h-32 resize-none"
              placeholder="Razlog suspenzije..."
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setSuspendModalOpen(false);
                  setSelectedListingId(null);
                  setActionReason('');
                }}
                className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
              >
                Otkaži
              </button>
              <button
                onClick={handleSuspend}
                disabled={!actionReason.trim()}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                Suspenduj
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {rejectModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Odbij oglas</h3>
            <p className="text-slate-600 dark:text-slate-400 mb-4">
              Unesite razlog odbijanja. Korisnik će biti obavešten.
            </p>
            <textarea
              value={actionReason}
              onChange={(e) => setActionReason(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white mb-4 h-32 resize-none"
              placeholder="Razlog odbijanja..."
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setRejectModalOpen(false);
                  setSelectedListingId(null);
                  setActionReason('');
                }}
                className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
              >
                Otkaži
              </button>
              <button
                onClick={handleReject}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Odbij
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reactivate Modal */}
      {reactivateModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Reaktiviraj oglas</h3>
            <p className="text-slate-600 dark:text-slate-400 mb-4">
              Unesite broj dana za koji želite da reaktivirate oglas.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Trajanje oglasa (u danima)
              </label>
              <input
                type="number"
                min="1"
                max="365"
                value={reactivateDays}
                onChange={(e) => setReactivateDays(Math.max(1, Math.min(365, Number(e.target.value) || 1)))}
                className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                placeholder="Unesite broj dana (1-365)"
              />
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Minimalno 1 dan, maksimalno 365 dana</p>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setReactivateModalOpen(false);
                  setSelectedListingId(null);
                  setReactivateDays(30);
                }}
                className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
              >
                Otkaži
              </button>
              <button
                onClick={confirmReactivate}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Reaktiviraj
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Extend/Shorten Duration Modal */}
      {extendModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Izmeni trajanje oglasa</h3>
            <p className="text-slate-600 dark:text-slate-400 mb-4">
              Unesite broj dana za izmenu trajanja. Pozitivan broj produžuje, negativan skraćuje.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Izmena trajanja (u danima)
              </label>
              <input
                type="number"
                min="-365"
                max="365"
                value={extendDays}
                onChange={(e) => setExtendDays(Math.max(-365, Math.min(365, Number(e.target.value) || 0)))}
                className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                placeholder="npr. 30 za produženje, -7 za skraćenje"
              />
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {extendDays > 0 
                  ? `Oglas će biti produžen za ${extendDays} dana` 
                  : extendDays < 0 
                    ? `Oglas će biti skraćen za ${Math.abs(extendDays)} dana` 
                    : 'Unesite broj dana za izmenu'}
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setExtendModalOpen(false);
                  setSelectedListingId(null);
                  setExtendDays(30);
                }}
                className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
              >
                Otkaži
              </button>
              <button
                onClick={confirmExtend}
                disabled={extendDays === 0}
                className={`px-4 py-2 rounded-lg ${extendDays > 0 ? 'bg-blue-600 hover:bg-blue-700' : extendDays < 0 ? 'bg-orange-600 hover:bg-orange-700' : 'bg-slate-400 cursor-not-allowed'} text-white`}
              >
                {extendDays > 0 ? 'Produži' : extendDays < 0 ? 'Skrati' : 'Izmeni'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
